import { useEffect, useState } from "react";
import { settingsApi, showSuccess, showError } from "../lib";
import {
  Settings,
  Mail,
  CreditCard,
  Users,
  BookOpen,
  Shield,
  Loader,
  Save,
  RotateCcw,
  Trash2,
  Database,
  AlertTriangle,
  UserX,
  FileX,
  HardDrive,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { adminApi } from "../lib";

interface SettingsData {
  general?: {
    platformName?: string;
    platformDescription?: string;
    supportEmail?: string;
    contactPhone?: string;
    timezone?: string;
    language?: string;
  };
  registration?: {
    allowRegistration?: boolean;
    requireEmailVerification?: boolean;
    defaultRole?: string;
  };
  course?: {
    requireApproval?: boolean;
    maxCoursePrice?: number;
    minCoursePrice?: number;
  };
  payment?: {
    paymentEnabled?: boolean;
    paymentGateway?: string;
    platformFee?: number;
  };
  email?: {
    emailNotificationsEnabled?: boolean;
    sendWelcomeEmail?: boolean;
  };
  moderation?: {
    autoModerateContent?: boolean;
    requireContentApproval?: boolean;
  };
}

const categories = [
  { id: "general", label: "General", icon: Settings },
  { id: "registration", label: "Registration", icon: Users },
  { id: "course", label: "Courses", icon: BookOpen },
  { id: "payment", label: "Payment", icon: CreditCard },
  { id: "email", label: "Email", icon: Mail },
  { id: "moderation", label: "Moderation", icon: Shield },
  { id: "security", label: "Security", icon: Shield },
];

interface SecurityAction {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  dangerLevel: "warning" | "danger";
  action: () => Promise<void>;
}

const securityActions: SecurityAction[] = [
  {
    id: "delete-all-users",
    label: "Delete All Users",
    description: "Permanently delete all user accounts except the current admin",
    icon: UserX,
    dangerLevel: "danger",
    action: async () => {
      await adminApi.deleteAllUsers();
    },
  },
  {
    id: "delete-all-courses",
    label: "Delete All Courses",
    description: "Permanently delete all courses and their content",
    icon: FileX,
    dangerLevel: "danger",
    action: async () => {
      await adminApi.deleteAllCourses();
    },
  },
  {
    id: "clear-database",
    label: "Clear Database",
    description: "Delete all data and reset the database",
    icon: Database,
    dangerLevel: "danger",
    action: async () => {
      await adminApi.clearDatabase();
    },
  },
  {
    id: "reset-everything",
    label: "Reset Everything",
    description: "Delete all data and start fresh",
    icon: HardDrive,
    dangerLevel: "danger",
    action: async () => {
      await adminApi.resetDatabase();
    },
  },
];

const AdminSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SettingsData>({});
  const [activeCategory, setActiveCategory] = useState("general");
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ action: SecurityAction; loading: boolean } | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const result = await settingsApi.getSettings();
      if (result.success) {
        setSettings(result.data || {});
      }
    } catch (err: any) {
      console.error("Failed to load settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (category: string, key: string, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...(prev as any)?.[category],
        [key]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await settingsApi.updateSettings(settings as any);
      if (result.success) {
        showSuccess("Settings saved successfully");
        setHasChanges(false);
      }
    } catch (err: any) {
      showError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (category: string, key: string, value: any, label: string, type: string = "text") => {
    const isBoolean = type === "boolean";
    
    if (isBoolean) {
      return (
        <label className="flex items-center gap-2 py-1 cursor-pointer">
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => handleChange(category, key, e.target.checked)}
            className="w-4 h-4 border-gray-300 rounded text-primary focus:ring-primary"
          />
          <span className="text-xs text-gray-700 sm:text-sm">{label}</span>
        </label>
      );
    }

    return (
      <div className="space-y-1">
        <label className="text-xs text-gray-600 sm:text-sm">{label}</label>
        <input
          type={type === "number" ? "number" : "text"}
          value={value || ""}
          onChange={(e) => handleChange(category, key, type === "number" ? parseFloat(e.target.value) : e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
        />
      </div>
    );
  };

  const renderCategoryContent = () => {
    const categorySettings = (settings as any)?.[activeCategory] || {};
    
    switch (activeCategory) {
      case "general":
        return (
          <div className="space-y-4">
            {renderField("general", "platformName", categorySettings.platformName, "Platform Name")}
            {renderField("general", "platformDescription", categorySettings.platformDescription, "Description")}
            {renderField("general", "supportEmail", categorySettings.supportEmail, "Support Email", "email")}
            {renderField("general", "contactPhone", categorySettings.contactPhone, "Contact Phone")}
            <div className="space-y-1">
              <label className="text-xs text-gray-600 sm:text-sm">Timezone</label>
              <select
                value={categorySettings.timezone || "UTC"}
                onChange={(e) => handleChange("general", "timezone", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">America/New_York</option>
                <option value="Europe/London">Europe/London</option>
                <option value="Asia/Tokyo">Asia/Tokyo</option>
              </select>
            </div>
          </div>
        );
      case "registration":
        return (
          <div className="space-y-4">
            {renderField("registration", "allowRegistration", categorySettings.allowRegistration, "Allow Registration", "boolean")}
            {renderField("registration", "requireEmailVerification", categorySettings.requireEmailVerification, "Require Email Verification", "boolean")}
            <div className="space-y-1">
              <label className="text-xs text-gray-600 sm:text-sm">Default Role</label>
              <select
                value={categorySettings.defaultRole || "student"}
                onChange={(e) => handleChange("registration", "defaultRole", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
              >
                <option value="student">Student</option>
                <option value="mentor">Mentor</option>
              </select>
            </div>
          </div>
        );
      case "course":
        return (
          <div className="space-y-4">
            {renderField("course", "requireApproval", categorySettings.requireApproval, "Require Course Approval", "boolean")}
            {renderField("course", "maxCoursePrice", categorySettings.maxCoursePrice, "Max Course Price", "number")}
            {renderField("course", "minCoursePrice", categorySettings.minCoursePrice, "Min Course Price", "number")}
          </div>
        );
      case "payment":
        return (
          <div className="space-y-4">
            {renderField("payment", "paymentEnabled", categorySettings.paymentEnabled, "Enable Payments", "boolean")}
            <div className="space-y-1">
              <label className="text-xs text-gray-600 sm:text-sm">Payment Gateway</label>
              <select
                value={categorySettings.paymentGateway || "stripe"}
                onChange={(e) => handleChange("payment", "paymentGateway", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
              >
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
                <option value="razorpay">Razorpay</option>
              </select>
            </div>
            {renderField("payment", "platformFee", categorySettings.platformFee, "Platform Fee (%)", "number")}
          </div>
        );
      case "email":
        return (
          <div className="space-y-4">
            {renderField("email", "emailNotificationsEnabled", categorySettings.emailNotificationsEnabled, "Enable Email Notifications", "boolean")}
            {renderField("email", "sendWelcomeEmail", categorySettings.sendWelcomeEmail, "Send Welcome Email", "boolean")}
          </div>
        );
      case "moderation":
        return (
          <div className="space-y-4">
            {renderField("moderation", "autoModerateContent", categorySettings.autoModerateContent, "Auto Moderate Content", "boolean")}
            {renderField("moderation", "requireContentApproval", categorySettings.requireContentApproval, "Require Content Approval", "boolean")}
          </div>
        );
      case "security":
        return (
          <div className="space-y-4">
            <div className="p-4 border border-red-100 rounded-lg bg-red-50">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-red-800">Danger Zone</h3>
                  <p className="mt-1 text-xs text-red-600">These actions are irreversible. Please proceed with caution.</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {securityActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => setConfirmModal({ action, loading: false })}
                  className="flex items-center w-full gap-3 p-4 text-left transition border border-red-200 rounded-lg hover:bg-red-50"
                >
                  <action.icon className="w-5 h-5 text-red-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{action.label}</p>
                    <p className="text-xs text-gray-500">{action.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      default:
        return <p className="text-sm text-gray-500">No settings available</p>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold text-black sm:text-lg">Platform Settings</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Configure your platform settings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchSettings}
            className="flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Mobile Category Selector */}
      <div className="block mb-4 lg:hidden">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="flex items-center justify-between w-full px-4 py-3 bg-white border border-gray-200 rounded-lg"
        >
          <div className="flex items-center gap-2">
            {categories.find(c => c.id === activeCategory)?.icon && 
              (() => {
                const Icon = categories.find(c => c.id === activeCategory)!.icon;
                return <Icon className="w-4 h-4 text-primary" />;
              })()}
            <span className="text-sm font-medium text-gray-900">
              {categories.find(c => c.id === activeCategory)?.label}
            </span>
          </div>
          {mobileMenuOpen ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronLeft className="w-4 h-4 text-gray-400" />}
        </button>

        {mobileMenuOpen && (
          <div className="mt-2 overflow-hidden bg-white border border-gray-200 rounded-lg">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                  {isActive && <CheckIcon className="w-4 h-4 ml-auto" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop Layout - Sidebar + Content */}
      <div className="flex flex-col gap-5 lg:flex-row">
        {/* Sidebar - Desktop only */}
        <div className="hidden w-56 lg:block shrink-0">
          <div className="sticky p-2 bg-white border border-gray-200 rounded-xl top-20">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition-all ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-5 bg-white border border-gray-200 rounded-xl sm:p-6">
          <h2 className="mb-4 text-sm font-semibold text-black sm:text-base">
            {categories.find((c) => c.id === activeCategory)?.label} Settings
          </h2>
          <div className="max-h-[calc(100vh-250px)] overflow-y-auto pr-2">
            {renderCategoryContent()}
          </div>
        </div>
      </div>

      {/* Confirmation Modal - Responsive */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md p-5 mx-4 bg-white rounded-xl sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 sm:text-lg">{confirmModal.action.label}</h3>
                <p className="text-xs text-gray-500 sm:text-sm">{confirmModal.action.description}</p>
              </div>
            </div>
            
            <div className="mb-5">
              <label className="block mb-2 text-xs font-medium text-gray-700 sm:text-sm">
                Type <span className="font-mono font-bold">CONFIRM</span> to verify
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary font-mono"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setConfirmModal(null);
                  setConfirmText("");
                }}
                className="flex-1 px-4 py-2.5 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (confirmText !== "CONFIRM") {
                    showError("Please type CONFIRM to verify");
                    return;
                  }
                  setConfirmModal((prev) => prev ? { ...prev, loading: true } : null);
                  try {
                    await confirmModal.action.action();
                    showSuccess(`${confirmModal.action.label} completed successfully`);
                    setConfirmModal(null);
                    setConfirmText("");
                  } catch (err: any) {
                    showError(err.message || "Action failed");
                    setConfirmModal((prev) => prev ? { ...prev, loading: false } : null);
                  }
                }}
                disabled={confirmModal.loading || confirmText !== "CONFIRM"}
                className="flex-1 px-4 py-2.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {confirmModal.loading ? (
                  <Loader className="w-4 h-4 mx-auto animate-spin" />
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for checkmark
const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default AdminSettingsPage;